/** @format */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, CircularProgress, Tooltip, Chip } from "@mui/material";
import {
    HealthAndSafety,
    Shield,
    DoNotDisturbOn,
    ReportProblemOutlined,
    Verified,
    CheckCircleOutline,
} from "@mui/icons-material";

import { CollectSectionCard } from "../styles";
import { colors } from "../../../utility/types/constant";
import { IRelationshipSafety } from "../../../utility/types/collectTypes";
import { submitGetRelationshipSafety } from "../../../redux/services/collectService";

const FONT = "'Montserrat', sans-serif";

interface Props {
    businessId: string | null;
    refreshKey?: number;
}

const TONE_COLOR = {
    friendly: "#15803d",
    professional: "#1d4ed8",
    firm: "#c2410c",
};

interface TileProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    sub?: string;
    accent: string;
}

const SafetyTile: React.FC<TileProps> = ({ icon, label, value, sub, accent }) => (
    <Box
        sx={{
            flex: 1,
            minWidth: { xs: "45%", md: 0 },
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            p: 1.5,
            backgroundColor: "#fff",
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.4 }}>
            <Box sx={{ color: accent, display: "inline-flex" }}>{icon}</Box>
            <Typography
                sx={{
                    fontFamily: FONT,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                }}
            >
                {label}
            </Typography>
        </Box>
        <Typography
            sx={{ fontFamily: FONT, fontSize: "1.4rem", fontWeight: 800, color: "#111827", lineHeight: 1.1 }}
        >
            {value}
        </Typography>
        {sub && (
            <Typography sx={{ fontFamily: FONT, fontSize: "0.72rem", color: "#6b7280", mt: 0.25 }}>
                {sub}
            </Typography>
        )}
    </Box>
);

const RelationshipSafetyPanel: React.FC<Props> = ({ businessId, refreshKey }) => {
    const [data, setData] = useState<IRelationshipSafety | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const requestSequence = useRef(0);

    const load = useCallback(async () => {
        const requestId = ++requestSequence.current;
        if (!businessId) {
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError("");
        setData(null);
        try {
            const res = await submitGetRelationshipSafety(businessId);
            if (requestId === requestSequence.current) setData(res);
        } catch (e: any) {
            if (requestId === requestSequence.current) {
                setError(e?.message || "Failed to load relationship safety.");
            }
        } finally {
            if (requestId === requestSequence.current) setLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        load();
        return () => {
            requestSequence.current += 1;
        };
    }, [load, refreshKey]);

    if (!businessId) return null;

    const tone = data?.tone;
    const shielded = data?.shielded;
    const optOuts = data?.optOuts;
    const concerns = data?.concerns;
    const reliable = data?.reliable;

    const shieldedSub = shielded
        ? [
            shielded.promiseToPay ? `${shielded.promiseToPay} promised` : "",
            shielded.disputed ? `${shielded.disputed} disputed` : "",
            shielded.manualReview ? `${shielded.manualReview} in review` : "",
        ]
            .filter(Boolean)
            .join(" · ") || "none paused right now"
        : "";

    return (
        <CollectSectionCard>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <HealthAndSafety sx={{ color: colors.primary }} />
                <Typography
                    sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.05rem", color: "#111827" }}
                >
                    Relationship Safety
                </Typography>
            </Box>
            <Typography
                sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#6b7280", mb: 1.5 }}
            >
                Recovering what you're owed without burning bridges.
            </Typography>

            {error && (
                <Typography sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#b91c1c", mb: 1 }}>
                    {error}
                </Typography>
            )}

            {loading && !data ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: colors.primary }} />
                </Box>
            ) : (
                <>
                    {/* Tone gentleness bar */}
                    {tone && tone.total > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 0.6,
                                }}
                            >
                                <Typography
                                    sx={{ fontFamily: FONT, fontSize: "0.76rem", fontWeight: 700, color: "#374151" }}
                                >
                                    Tone across active accounts
                                </Typography>
                                {tone.gentlePct !== null && (
                                    <Typography
                                        sx={{ fontFamily: FONT, fontSize: "0.76rem", fontWeight: 800, color: TONE_COLOR.friendly }}
                                    >
                                        {tone.gentlePct}% on a gentle tone
                                    </Typography>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    height: 12,
                                    borderRadius: "6px",
                                    overflow: "hidden",
                                    backgroundColor: "#f1f5f9",
                                }}
                            >
                                {(["friendly", "professional", "firm"] as const).map((k) => {
                                    const pct = tone.total > 0 ? (tone[k] / tone.total) * 100 : 0;
                                    if (pct <= 0) return null;
                                    return (
                                        <Tooltip
                                            key={k}
                                            arrow
                                            title={`${tone[k]} ${k} (${Math.round(pct)}%)`}
                                        >
                                            <Box sx={{ width: `${pct}%`, backgroundColor: TONE_COLOR[k] }} />
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5, mt: 0.6, flexWrap: "wrap" }}>
                                {(["friendly", "professional", "firm"] as const).map((k) => (
                                    <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                                        <Box sx={{ width: 9, height: 9, borderRadius: "2px", backgroundColor: TONE_COLOR[k] }} />
                                        <Typography sx={{ fontFamily: FONT, fontSize: "0.7rem", color: "#6b7280", textTransform: "capitalize" }}>
                                            {k} ({tone[k]})
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Stat tiles */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: { xs: "wrap", md: "nowrap" } }}>
                        <SafetyTile
                            icon={<Shield fontSize="small" />}
                            label="Shielded from chasing"
                            value={`${shielded?.total ?? 0}`}
                            sub={shieldedSub}
                            accent="#15803d"
                        />
                        <SafetyTile
                            icon={<DoNotDisturbOn fontSize="small" />}
                            label="Opt-outs respected"
                            value={`${optOuts?.total ?? 0}`}
                            sub={
                                optOuts && optOuts.total > 0
                                    ? `${optOuts.email} email · ${optOuts.sms} SMS`
                                    : "no opt-outs"
                            }
                            accent="#6b7280"
                        />
                        <SafetyTile
                            icon={<ReportProblemOutlined fontSize="small" />}
                            label="Concerns caught"
                            value={`${concerns?.total ?? 0}`}
                            sub={
                                concerns && concerns.total > 0
                                    ? `${concerns.disputes} disputes · ${concerns.complaints} complaints`
                                    : "none flagged"
                            }
                            accent="#c2410c"
                        />
                        <SafetyTile
                            icon={<Verified fontSize="small" />}
                            label="Reliable, treated gently"
                            value={`${reliable?.count ?? 0}`}
                            sub={
                                reliable && reliable.avgOnTimeRate !== null
                                    ? `${reliable.avgOnTimeRate}% on-time`
                                    : "good payers"
                            }
                            accent="#1d4ed8"
                        />
                    </Box>

                    {/* Guardrails footer */}
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.75 }}>
                        {[
                            "Max 1 reminder per day",
                            "Opt-outs always respected",
                            "Auto-pause on disputes & promises",
                        ].map((g) => (
                            <Chip
                                key={g}
                                icon={<CheckCircleOutline sx={{ fontSize: "0.9rem !important", color: "#15803d !important" }} />}
                                label={g}
                                size="small"
                                sx={{
                                    backgroundColor: "#f0fdf4",
                                    color: "#166534",
                                    fontFamily: FONT,
                                    fontWeight: 600,
                                    fontSize: "0.68rem",
                                    "& .MuiChip-icon": { ml: 0.5 },
                                }}
                            />
                        ))}
                    </Box>
                </>
            )}
        </CollectSectionCard>
    );
};

export default RelationshipSafetyPanel;
